import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from './ui';
import { HttpError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/**
 * Create-an-additional-workspace dialog (Enterprise: up to two organization
 * workspaces). On success it switches to the new workspace and lands on its
 * onboarding wizard so the new org gets its own profile.
 */
export function NewWorkspaceModal({ onClose }: { onClose: () => void }) {
  const { createOrg } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the workspace a name first.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createOrg(trimmed);
      onClose();
      navigate('/app/onboarding', { replace: true });
    } catch (err) {
      setError(
        err instanceof HttpError
          ? err.message
          : 'Could not create the workspace. Please try again.',
      );
      setSaving(false);
    }
  };

  return (
    <Modal title="New organization workspace" onClose={onClose}>
      <form onSubmit={submit}>
        <p className="sub" style={{ marginTop: 0 }}>
          Enterprise covers up to two organization workspaces under one
          subscription. The new workspace starts fresh — you'll set up its
          profile next.
        </p>
        <div className="field">
          <label className="field-label" htmlFor="new-workspace-name">
            Workspace name
          </label>
          <input
            id="new-workspace-name"
            className="input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. HDG Community Foundation"
            maxLength={120}
            autoFocus
            disabled={saving}
          />
        </div>
        {error && (
          <div className="alert alert-error" role="alert" style={{ marginBottom: 'var(--s4)' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--s3)', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create workspace'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
